import { useEffect, useRef, useState } from 'react';

/* ─────────────────────────────────────────
   FLOW ANIMATION
   Plays a 3-act storyboard:
   Act 1 → HR fills the role brief (form fields type in)
   Act 2 → AI generates the career page (progress bar + reveal)
   Act 3 → HR edits the live page via chat

   Auto-loops. Each act ~3.5s.
───────────────────────────────────────── */

const ACTS = ['brief', 'generating', 'editing'];
const ACT_DURATION = 3800; // ms per act

function useLoop(n, duration) {
  const [step, setStep]   = useState(0);
  const [prog, setProg]   = useState(0);
  const raf               = useRef(null);
  const startRef          = useRef(null);

  useEffect(() => {
    let actIndex = 0;

    function tick(ts) {
      if (!startRef.current) startRef.current = ts;
      const elapsed = ts - startRef.current;
      const pct = Math.min(elapsed / duration, 1);
      setProg(pct);

      if (pct >= 1) {
        actIndex = (actIndex + 1) % n;
        setStep(actIndex);
        setProg(0);
        startRef.current = null;
      }

      raf.current = requestAnimationFrame(tick);
    }

    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [n, duration]);

  return [step, prog];
}

/* ─── ACT 1: Brief form filling ─── */
function ActBrief({ prog }) {
  const fields = [
    { label: 'Role title',   value: 'Senior React Developer' },
    { label: 'Company',      value: 'Acme Labs' },
    { label: 'Salary range', value: 'USD 80k – 110k' },
    { label: 'Work mode',    value: 'Remote / Hybrid' },
  ];

  return (
    <div className="fa-panel fa-brief">
      <div className="fa-panel-eyebrow">
        <span className="fa-step-dot fa-dot-yellow" /> Step 01 — Fill the role brief
      </div>
      <div className="fa-form-mock">
        {fields.map((f, i) => {
          const threshold = i / fields.length;
          const show = prog > threshold;
          const fieldProg = show
            ? Math.min((prog - threshold) / (1 / fields.length), 1)
            : 0;
          const chars = Math.floor(fieldProg * f.value.length);
          return (
            <div key={f.label} className={`fa-field${show ? ' fa-field-in' : ''}`}>
              <span className="fa-field-label">{f.label}</span>
              <span className="fa-field-value">
                {f.value.slice(0, chars)}
                {show && chars < f.value.length && <span className="fa-cursor" />}
              </span>
            </div>
          );
        })}
        <div className="fa-form-btn-row" style={{ opacity: prog > 0.85 ? (prog - 0.85) / 0.15 : 0 }}>
          <div className="fa-form-btn">Generate career page →</div>
        </div>
      </div>
    </div>
  );
}

/* ─── ACT 2: Generating ─── */
function ActGenerating({ prog }) {
  const lines = [
    'Composing hero section...',
    'Building qualifications block...',
    'Adding application form...',
    'Deploying live page...',
  ];
  const showLines = Math.floor(prog * (lines.length + 0.99));

  return (
    <div className="fa-panel fa-generating">
      <div className="fa-panel-eyebrow">
        <span className="fa-step-dot fa-dot-mint" /> Step 02 — Generating career page
      </div>
      <div className="fa-gen-shell">
        <div className="fa-gen-progress-track">
          <div
            className="fa-gen-progress-fill"
            style={{ width: `${prog * 100}%` }}
          />
        </div>
        <div className="fa-gen-pct">{Math.round(prog * 100)}%</div>
        <div className="fa-gen-log">
          {lines.slice(0, showLines).map((l, i) => (
            <div key={l} className="fa-gen-log-line" style={{ animationDelay: `${i * 40}ms` }}>
              <span className="fa-gen-tick">✓</span> {l}
            </div>
          ))}
          {showLines <= lines.length && (
            <div className="fa-gen-log-line fa-gen-active">
              <span className="fa-spinner-inline" /> Processing...
            </div>
          )}
        </div>

        {/* page preview fades in at 70% */}
        <div className="fa-page-preview" style={{ opacity: prog > 0.65 ? (prog - 0.65) / 0.35 : 0, transform: `translateY(${prog > 0.65 ? (1 - (prog - 0.65) / 0.35) * 12 : 12}px)` }}>
          <div className="fa-pp-tag">Now Hiring · Remote</div>
          <div className="fa-pp-title">Senior React Developer</div>
          <div className="fa-pp-salary">USD 80k – 110k / year</div>
          <div className="fa-pp-bar" />
          <div className="fa-pp-apply">Apply now →</div>
        </div>
      </div>
    </div>
  );
}

/* ─── ACT 3: Editing ─── */
function ActEditing({ prog }) {
  const edits = [
    { type: 'note', text: 'Change CTA to "Join the team"' },
    { type: 'del',  text: '− Apply now →' },
    { type: 'add',  text: '+ Join the team →' },
    { type: 'note', text: 'Make salary bold and larger' },
    { type: 'add',  text: '+ font-weight: 800; font-size: 1.4rem' },
  ];
  const showEdits = Math.floor(prog * (edits.length + 0.6));
  const inputVisible = prog > 0.3;
  const inputText = 'Change heading color to yellow';
  const inputChars = inputVisible
    ? Math.floor(((prog - 0.3) / 0.7) * inputText.length)
    : 0;

  return (
    <div className="fa-panel fa-editing">
      <div className="fa-panel-eyebrow">
        <span className="fa-step-dot fa-dot-mint fa-dot-pulse" /> Step 03 — Live editing via chat
      </div>
      <div className="fa-edit-shell">
        <div className="fa-edit-split">
          {/* left: diff log */}
          <div className="fa-diff-col">
            <div className="fa-diff-header">Diff view</div>
            <div className="fa-diff-log">
              {edits.slice(0, showEdits).map((e, i) => (
                <div
                  key={i}
                  className={`fa-diff-line fa-diff-${e.type}`}
                  style={{ animationDelay: `${i * 60}ms` }}
                >
                  {e.text}
                </div>
              ))}
            </div>
            <div className="fa-diff-input">
              <span className="fa-diff-inp-text">
                {inputText.slice(0, inputChars)}
                {inputVisible && inputChars < inputText.length && <span className="fa-cursor" />}
              </span>
              {inputChars >= inputText.length && (
                <span className="fa-diff-send">→</span>
              )}
            </div>
          </div>

          {/* right: live page updating */}
          <div className="fa-live-col">
            <div className="fa-diff-header">
              <span className="fa-dot-live-small" /> Live page
            </div>
            <div className="fa-live-page">
              <div className="fa-lp-tag">Now Hiring · Remote</div>
              <div
                className="fa-lp-title"
                style={{ color: prog > 0.6 ? 'var(--fa-yellow)' : 'var(--fa-text)' }}
              >
                SENIOR REACT<br />DEVELOPER
              </div>
              <div className="fa-lp-salary"
                style={{ fontWeight: prog > 0.75 ? 800 : 400, fontSize: prog > 0.75 ? '13px' : '11px' }}>
                USD 80k – 110k / year
              </div>
              <div className="fa-lp-apply">
                {showEdits >= 3 ? 'Join the team →' : 'Apply now →'}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── CONNECTOR ARROW ─── */
function Arrow({ active }) {
  return (
    <div className={`fa-arrow${active ? ' fa-arrow-active' : ''}`}>
      <div className="fa-arrow-line" />
      <div className="fa-arrow-head">›</div>
    </div>
  );
}

/* ─── MAIN EXPORT ─── */
export default function FlowAnimation() {
  const [actIndex, prog] = useLoop(ACTS.length, ACT_DURATION);
  const act = ACTS[actIndex];

  return (
    <section className="fa-section">
      <div className="fa-section-header">
        <div className="section-eyebrow">The workflow</div>
        <div className="section-h">From brief to live page in one flow</div>
      </div>

      <div className="fa-stage">
        {/* Step indicators */}
        <div className="fa-steps-nav">
          {ACTS.map((a, i) => (
            <div key={a} className={`fa-step-nav-item${actIndex === i ? ' active' : actIndex > i ? ' done' : ''}`}>
              <div className="fa-step-nav-num">{String(i + 1).padStart(2, '0')}</div>
              <div className="fa-step-nav-label">
                {a === 'brief' ? 'Fill brief' : a === 'generating' ? 'AI generates' : 'Edit live'}
              </div>
              <div className="fa-step-nav-bar">
                <div
                  className="fa-step-nav-fill"
                  style={{ width: actIndex === i ? `${prog * 100}%` : actIndex > i ? '100%' : '0%' }}
                />
              </div>
            </div>
          ))}
        </div>

        {/* Act panels with connectors */}
        <div className="fa-acts">
          <ActBrief prog={act === 'brief' ? prog : actIndex > 0 ? 1 : 0} />
          <Arrow active={actIndex >= 1} />
          <ActGenerating prog={act === 'generating' ? prog : actIndex > 1 ? 1 : 0} />
          <Arrow active={actIndex >= 2} />
          <ActEditing prog={act === 'editing' ? prog : 0} />
        </div>

        {/* Loop label */}
      </div>
    </section>
  );
}
